// frontend/src/modules/onboarding/TourProvider.tsx
//
// Runs a list of stops: navigate to the stop's route, wait for its
// `data-tour` anchor to appear, then spotlight it.
//
// THE IMPORTANT BEHAVIOUR: if an anchor never appears within
// ANCHOR_TIMEOUT_MS the stop is SKIPPED and a warning is logged. Anchors live
// on buttons that future work will rename, hide behind a permission, or move.
// An overlay pointing at nothing — or one that traps someone because its
// target never rendered — is far worse than no onboarding. The engine fails by
// getting out of the way. That same rule bounds navigation too: if a route
// guard keeps bouncing the tour away from a stop's route, we give up after
// ANCHOR_TIMEOUT_MS rather than re-navigating forever with `active` stuck true.

import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spotlight } from './Spotlight';

// 8s, not 2.5s: on a brand-new tenant's very first navigation a cold
// serverless API + the target page's own loading gate can easily exceed 2.5s,
// and a single-stop tour that skips its only stop ends invisibly — the user
// experiences "the guide doesn't exist".
export const ANCHOR_TIMEOUT_MS = 8000;

export interface TourStop {
  anchor: string;
  route: string;
  title: string;
  body: string;
}

interface TourApi {
  start: (stops: TourStop[]) => void;
  stop: () => void;
  next: () => void;
  active: boolean;
  current: TourStop | null;
  index: number;
  total: number;
}

const TourContext = React.createContext<TourApi | null>(null);

export function useTour(): TourApi {
  const ctx = React.useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}

/**
 * Wait for an anchor to appear, then invoke `onResolve` with the element (or
 * `null` on timeout). Delivers the result via a direct callback rather than a
 * Promise: a `.then()` continuation is a microtask, and under fake timers a
 * caller advancing time in several back-to-back synchronous steps (no
 * `await` between them) would never see that microtask drain in between —
 * the timeout would appear to fire but the resulting skip/advance wouldn't
 * happen until some later, unrelated `await`. Calling straight into
 * `onResolve` keeps the whole thing inside the same `act()`/timer tick that
 * fired it. Returns a cleanup function.
 */
function waitForAnchor(
  anchor: string,
  timeoutMs: number,
  onResolve: (el: HTMLElement | null) => void,
): () => void {
  const sel = `[data-tour="${anchor}"]`;
  const found = document.querySelector<HTMLElement>(sel);
  if (found) {
    onResolve(found);
    return () => {};
  }

  const timer = setTimeout(() => { obs.disconnect(); onResolve(null); }, timeoutMs);
  const obs = new MutationObserver(() => {
    const el = document.querySelector<HTMLElement>(sel);
    if (el) { clearTimeout(timer); obs.disconnect(); onResolve(el); }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  return () => { clearTimeout(timer); obs.disconnect(); };
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stops, setStops] = React.useState<TourStop[]>([]);
  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  const active = stops.length > 0;
  const current = active ? stops[index] ?? null : null;

  // The element that had focus before the tour opened, so it can be
  // restored when the tour ends (Escape, backdrop click, or running out of
  // stops) instead of leaving focus stranded wherever the last card was.
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  // The single place that actually ends a tour. Both an explicit stop() and
  // "advanced past the last stop" funnel through here so focus is restored
  // either way.
  const end = React.useCallback(() => {
    setStops([]);
    setIndex(0);
    setRect(null);
    const toFocus = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;
    toFocus?.focus();
  }, []);

  const stop = React.useCallback(() => { end(); }, [end]);

  const start = React.useCallback((next: TourStop[]) => {
    if (next.length === 0) return;
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStops(next);
    setIndex(0);
    setRect(null);
  }, []);

  // Compute the next index from the current render's `stops`/`index` and
  // call the setters directly, rather than deriving it inside the
  // `setIndex` updater (which also called `setStops`). Updaters can be
  // replayed by React (StrictMode double-invokes them), so an updater with a
  // side effect on a *different* piece of state is a concurrent-rendering
  // hazard even though it happened to be idempotent here.
  const advance = React.useCallback(() => {
    const nextIndex = index + 1;
    setRect(null);
    if (nextIndex >= stops.length) {
      end();
    } else {
      setIndex(nextIndex);
    }
  }, [index, stops.length, end]);

  // Keep a ref to the latest pathname so the navigation-deadline timer below
  // can check "did we ever get there" without needing to be re-created every
  // time the pathname changes.
  const pathnameRef = React.useRef(location.pathname);
  React.useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);

  // A stop's route may carry a query string (e.g. /dashboard/settings?section=company,
  // which is how the settings walkthrough reaches a section that is not the
  // default one). location.pathname never contains the search, so every
  // "are we there yet" comparison must be against the pathname half only —
  // otherwise such a stop looks permanently un-reached and we re-navigate in a
  // loop until the deadline fires.
  const currentPath = current ? current.route.split('?')[0] : null;

  // Retry navigation to the current stop's route on every pathname change
  // (e.g. after a route guard bounces us elsewhere).
  React.useEffect(() => {
    if (!current || !currentPath) return;
    if (location.pathname !== currentPath) {
      navigate(current.route);
    }
  }, [current, currentPath, location.pathname, navigate]);

  // Bound how long we wait to actually land on the stop's route. Armed
  // unconditionally whenever `current` changes — even if we're already on
  // the right route — because arming it only when we start out elsewhere
  // left a hole: a tour that starts already on the stop's route has no
  // deadline armed at all, so if a route guard bounces it away *after*
  // that, the retry effect (which does depend on location.pathname) would
  // re-navigate forever with `active` stuck true — the exact failure this
  // bound exists to prevent, just reached one step later. The fire-time
  // check below (`pathnameRef.current !== current.route`) makes this a
  // no-op whenever we're already on the right route, so arming it
  // unconditionally is safe. Keyed only on `current` (not on
  // location.pathname) so a route guard that keeps bouncing us away
  // doesn't reset the clock on every bounce — the deadline covers the
  // whole time this stop has been waiting, not each individual
  // re-navigation attempt.
  React.useEffect(() => {
    if (!current) return;

    const timer = setTimeout(() => {
      if (pathnameRef.current !== current.route.split('?')[0]) {
        // eslint-disable-next-line no-console
        console.warn(`[tour] could not reach route "${current.route}" for anchor "${current.anchor}" — skipping this step`);
        advance();
      }
    }, ANCHOR_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // Deliberately excludes location.pathname — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, advance]);

  // Once we're on the right route, wait for the anchor to appear.
  React.useEffect(() => {
    if (!current || !currentPath) return;
    if (location.pathname !== currentPath) return;

    const cleanup = waitForAnchor(current.anchor, ANCHOR_TIMEOUT_MS, (el) => {
      if (!el) {
        // eslint-disable-next-line no-console
        console.warn(`[tour] anchor "${current.anchor}" never appeared — skipping this step`);
        advance();
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
      setRect(el.getBoundingClientRect());
    });

    return cleanup;
  }, [current, currentPath, location.pathname, advance]);

  // Keep the cutout aligned while the page moves under it.
  React.useEffect(() => {
    if (!current || !rect) return;
    const reposition = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.anchor}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [current, rect]);

  // Escape always exits. A tour must never be a trap.
  React.useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') stop(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, stop]);

  const value: TourApi = {
    start, stop, next: advance, active, current, index, total: stops.length,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {current && rect && (
        <Spotlight
          rect={rect}
          title={current.title}
          body={current.body}
          index={index}
          total={stops.length}
          onNext={advance}
          onSkipStep={advance}
          onClose={stop}
        />
      )}
    </TourContext.Provider>
  );
}
